import React, { useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { Button, Icon, Space } from '@sravni/react-design-system';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { Link, Text } from '@sravni/react-design-system/lib/Typography';
import { ArrowRight, Edit } from '@sravni/react-icons';
import { useIsPhone, useStyles } from '@sravni/react-utils';

import { yupResolver } from '@hookform/resolvers/yup';

import { sendAuthEvent, sendReviewEvent } from '../../../../../helpers/analyticsEvents';
import {
  usePhoneConfirmationStatus,
  usePhoneLoadingStatus,
  useSmsCodeStatusError,
  useSmsCodeStatusTimeout,
} from '../../../../../store/hooks/authorization';
import {
  setPhoneConfirmationStatus,
  setSmsCodeStatusError,
  trySendSms,
  trySubmitSms,
} from '../../../../../store/reducers/authorization';
import { PHONE_MASK } from '../../../../../utils/validation';
import { FormControl } from '../../../../UI/FormControl';
import { EVENT_ACTIONS } from '../../../helpers/analytics';
import { useFieldChanged } from '../../../hooks/useFieldChanged';
import { makeLabelRequired } from '../../../utils/makeLabelRequired';

import styles from './authorization-step.module.scss';
import { AUTH_FIELDS } from './constants';
import { createSchema, TAuthFormInputs } from './rules';

type TAuthorizationStepProps = {
  onChangeAgreement: (value: boolean) => void;
  onClickSubmitAuthButton: () => void;
  dataFromExtraInfo: Partial<TAuthFormInputs>;
  eventLabel: string[];
  onSuccessAuth?: () => void;
};

export const AuthorizationStep: React.FC<TAuthorizationStepProps> = (props) => {
  const { dataFromExtraInfo, onChangeAgreement, onClickSubmitAuthButton, eventLabel, onSuccessAuth } = props;

  const cx = useStyles(styles);
  const dispatch = useDispatch();
  const isPhone = useIsPhone();

  const isDataFromExtraInfo = useMemo(() => Object.entries(dataFromExtraInfo).filter(Boolean).length > 0, [
    dataFromExtraInfo,
  ]);

  const phoneConfirmationStatus = usePhoneConfirmationStatus();
  const smsCodeStatusError = useSmsCodeStatusError();
  const isLoading = usePhoneLoadingStatus();
  const smsCodeStatusTimeout = useSmsCodeStatusTimeout();

  const isTimeoutRunning = smsCodeStatusTimeout > 0;
  const isCodeSent = phoneConfirmationStatus === 'waiting';
  const isCodeSubmitted = phoneConfirmationStatus === 'completed';
  const isCodeRequired = phoneConfirmationStatus === 'required';
  const canResendSms = !isTimeoutRunning && isCodeSent;
  const isShowCodeLogic = isCodeSent || isCodeSubmitted;
  const isFieldDisabled = isLoading || isCodeSubmitted;

  const schema = createSchema({ isCodeSent });

  const {
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
    setError,
    setValue,
    getValues,
  } = useForm<TAuthFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...dataFromExtraInfo,
      [AUTH_FIELDS.AGREEMENT]: true,
    },
  });

  const codeValue = useWatch({ name: AUTH_FIELDS.CODE, control });

  useEffect(() => {
    if (smsCodeStatusError) {
      setError(AUTH_FIELDS.CODE, { message: smsCodeStatusError });
    }
  }, [smsCodeStatusError, setError]);

  const onSubmit: SubmitHandler<TAuthFormInputs> = useCallback(
    (formValues) => {
      onClickSubmitAuthButton();
      if (isCodeSent) {
        sendAuthEvent();
        dispatch(trySubmitSms({ values: formValues, onSuccessAuth }));
      } else if (!isTimeoutRunning) {
        sendReviewEvent({ label: eventLabel, action: EVENT_ACTIONS.GET_CODE });
        dispatch(trySendSms(formValues));
      }
    },
    [dispatch, eventLabel, isCodeSent, isTimeoutRunning, onClickSubmitAuthButton, onSuccessAuth],
  );

  const handleResendSms = useCallback(() => {
    dispatch(setPhoneConfirmationStatus('required'));
    setValue(AUTH_FIELDS.CODE, '');
    clearErrors(AUTH_FIELDS.CODE);

    handleSubmit(onSubmit)();
  }, [clearErrors, dispatch, handleSubmit, onSubmit, setValue]);

  const handleChangePhone = useCallback(() => {
    dispatch(setPhoneConfirmationStatus('required'));
    dispatch(setSmsCodeStatusError(null));
    setValue(AUTH_FIELDS.CODE, '');
    clearErrors(AUTH_FIELDS.CODE);
  }, [clearErrors, dispatch, setValue]);

  useEffect(() => {
    if (codeValue?.length === 4) {
      handleSubmit(onSubmit)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeValue]);

  const timerToText = `00:${smsCodeStatusTimeout < 10 ? '0' : ''}${smsCodeStatusTimeout}`;
  const isCodeHintVisible = isTimeoutRunning || canResendSms;

  useFieldChanged({
    name: AUTH_FIELDS.AGREEMENT,
    control,
    getValues,
    onChange: ({ value }) => {
      onChangeAgreement(value);
    },
  });

  useEffect(() => {
    const { [AUTH_FIELDS.AGREEMENT]: agreement } = getValues();
    onChangeAgreement(agreement);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getValues]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Text size={16} strong>
        Войдите на Сравни, чтобы опубликовать отзыв
      </Text>
      {isDataFromExtraInfo && (
        <Text size={14} className={'h-mt-8'}>
          Мы подставили ваши данные, но вы можете указать их заново
        </Text>
      )}

      <Space direction={isPhone ? 'vertical' : 'horizontal'} size={16} className={'h-mt-24'}>
        <Row gutter={[16, 16]} className={cx('row')}>
          <Col span={isPhone ? 12 : 4}>
            <FormControl
              controlType="text"
              name={AUTH_FIELDS.NAME}
              control={control}
              label={makeLabelRequired('Ваше имя')}
              error={errors?.[AUTH_FIELDS.NAME]}
              disabled={isFieldDisabled}
            />
          </Col>
          <Col span={isPhone ? 12 : 4}>
            <FormControl
              controlType="text"
              name={AUTH_FIELDS.EMAIL}
              control={control}
              label={makeLabelRequired('Email')}
              error={errors?.[AUTH_FIELDS.EMAIL]}
              disabled={isFieldDisabled}
            />
          </Col>
          <Col span={isPhone ? 12 : 4}>
            <FormControl
              controlType="mask"
              name={AUTH_FIELDS.PHONE}
              control={control}
              label={makeLabelRequired('Номер телефона')}
              error={errors?.[AUTH_FIELDS.PHONE]}
              icon={
                isCodeSent &&
                !isLoading && (
                  <Icon
                    icon={<Edit />}
                    size={24}
                    color={'blue'}
                    onClick={handleChangePhone}
                    className={cx('changePhoneIcon')}
                  />
                )
              }
              mask={PHONE_MASK}
              disabled={isFieldDisabled || isCodeSent}
            />
          </Col>
        </Row>
        {isCodeRequired && (
          <Button
            onClick={handleSubmit(onSubmit)}
            size={60}
            variant={'primary'}
            disabled={isFieldDisabled || isTimeoutRunning}
            loading={isLoading}
          >
            {isTimeoutRunning ? timerToText : 'Получить код'}
          </Button>
        )}
      </Space>

      {isShowCodeLogic && (
        <>
          <Space direction={'horizontal'} size={8} className={'h-mt-16'}>
            <FormControl
              controlType="text"
              name={AUTH_FIELDS.CODE}
              control={control}
              label={'Введите код из смс'}
              error={errors?.[AUTH_FIELDS.CODE]}
              className={cx('codeField')}
              disabled={isFieldDisabled}
            />
            <Button
              onClick={handleSubmit(onSubmit)}
              size={60}
              variant={'primary'}
              disabled={isFieldDisabled}
              loading={isLoading}
            >
              <Icon size={24} strong icon={<ArrowRight />} />
            </Button>
          </Space>
          {isCodeHintVisible && (
            <div className={cx('codeHint')}>
              {isTimeoutRunning && (
                <Text size={12} className={'h-color-D60'}>
                  Получить новый код можно через {timerToText}
                </Text>
              )}
              {canResendSms && (
                <Text size={12}>
                  <Link onClick={handleResendSms}>Запросить новый код</Link>
                </Text>
              )}
            </div>
          )}
        </>
      )}

      <FormControl
        controlType="checkbox"
        name={AUTH_FIELDS.AGREEMENT}
        control={control}
        label={
          <Text size={12}>
            При входе вы даёте согласие на{'\u00A0'}
            <Link href="https://www.sravni.ru/promo/review-rules/" target={'_blank'} rel={'noreferrer'}>
              публикацию отзыва
            </Link>
            ,{' '}
            <Link
              href="https://www.sravni.ru/promo/soglasie-na-obrabotku-personalnyh-dannyh/"
              target={'_blank'}
              rel={'noreferrer'}
            >
              обработку данных
            </Link>{' '}
            и{'\u00A0'}
            <Link
              href={'https://f.sravni.ru/cms/file/FilePath/soglasie-na-poluchenie-reklamy.pdf'}
              target={'_blank'}
              rel={'noreferrer'}
            >
              рекламу
            </Link>
          </Text>
        }
        error={errors?.[AUTH_FIELDS.AGREEMENT]}
        disabled={isLoading}
        className={'h-mt-24'}
      />
    </form>
  );
};
